import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  ArrowRight,
  Compass,
  Map,
  Rocket,
  Users,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

export const ForStudents = () => {
  const [expandedService, setExpandedService] = useState(null);

  // Where are you stuck cards
  const stuckCards = [
    {
      id: "direction",
      title: "I don't know which career fits me",
      cta: "Find direction",
      href: "/apply?plan=clarity-session",
      icon: Compass,
    },
    {
      id: "plan",
      title: "I know the direction, but need a plan",
      cta: "Build my roadmap",
      href: "/apply?plan=career-blueprint",
      icon: Map,
    },
    {
      id: "ready",
      title: "I know my goal, but I'm not job-ready",
      cta: "Get career-ready",
      href: "/apply?plan=career-readiness",
      icon: Rocket,
    },
    {
      id: "opportunities",
      title: "I'm ready and looking for opportunities",
      cta: "Join Talent Network",
      href: "/network",
      icon: Users,
    },
  ];

  // Services as solutions
  const services = [
    {
      id: 'clarity-session',
      name: 'Career Clarity Session',
      label: 'DIAGNOSE',
      labelColor: 'bg-amber-100 text-amber-700',
      price: '₹499', was: '₹649',
      duration: '45 min',
      description: "Understand what's holding you back and leave knowing what to focus on next.",
      includes: [
        'Review of your Career Readiness Check',
        '45-minute 1:1 career conversation',
        'Identify your 1-2 biggest career blockers',
        'Understand whether the issue is direction, profile, interview readiness or job search',
        'Clear recommendation on what to focus on next'
      ],
      cta: 'Get Career Clarity',
      href: '/apply?plan=clarity-session',
    },
    {
      id: 'direction-blueprint',
      name: 'Career Direction Blueprint',
      label: 'PLAN',
      labelColor: 'bg-teal-50 text-teal-700',
      price: '₹2,999', was: '₹3,499',
      duration: '1-2 weeks',
      description: 'Get a personalised career roadmap covering your best-fit roles, key gaps and exactly what to work on over the next 30-60 days.',
      includes: [
        'Detailed career and profile assessment',
        '2-3 recommended target roles',
        'Strength and skills-gap analysis',
        'Profile positioning recommendations',
        'Priority skills to develop',
        'Job-search direction',
        'Personalised 30-60 day action roadmap',
        'Written Career Direction Blueprint',
        'One follow-up review session'
      ],
      cta: 'Build My Roadmap',
      href: '/apply?plan=career-blueprint',
    },
    {
      id: 'readiness-program',
      name: 'Career Readiness Program',
      label: 'EXECUTE',
      labelColor: 'bg-emerald-100 text-emerald-700',
      price: '₹5,999', was: '₹7,499',
      duration: '4-6 weeks',
      description: 'Turn your roadmap into action with structured support to strengthen your profile, interviews and job-search execution.',
      includes: [
        'Profile and resume improvement',
        'LinkedIn/profile positioning',
        'Interview preparation',
        'Mock interviews and feedback',
        'Job-search strategy',
        'Application and outreach guidance',
        'Accountability and progress reviews',
        'Structured 4-6 week execution support'
      ],
      cta: 'Get Career Ready',
      href: '/apply?plan=career-readiness',
    },
  ];

  // Testimonials with outcome labels
  const testimonials = [
    { 
      outcome: "Got clarity on role fit",
      quote: "Got clearer on which roles to focus on and what to work on next.", 
      name: "Shivani", 
      detail: "B.Tech, Dehradun" 
    },
    { 
      outcome: "Improved interview confidence",
      quote: "I struggled to present myself in interviews. The feedback helped me communicate my work more clearly.", 
      name: "Rahul", 
      detail: "MCA, Jaipur" 
    },
    { 
      outcome: "Found the right direction",
      quote: "The prep helped me approach interviews with more structure. Got selected.", 
      name: "Satyam", 
      detail: "MBA, Pune" 
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* ============================================
          SECTION 1: HERO
          ============================================ */}
      <section className="bg-[#0b1f4d] text-white pt-24 sm:pt-20 lg:pt-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Left: Content */}
            <div className="text-center lg:text-left">
              {/* Eyebrow */}
              <p className="text-xs font-semibold tracking-widest text-teal-300 mb-4">
                FOR STUDENTS & GRADUATES
              </p>

              {/* Headline */}
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
                Not sure what your next career move should be?
              </h1>

              {/* Supporting copy */}
              <p className="text-lg text-slate-300 mb-6 max-w-lg mx-auto lg:mx-0">
                Whether you're confused about direction, struggling to get shortlisted, or preparing for interviews — start by finding the gap.
              </p>

              {/* Primary CTA */}
              <div className="flex flex-col items-center lg:items-start">
                <Link to="/career-readiness-check">
                  <Button 
                    className="bg-teal-400 text-[#0b1f4d] hover:bg-teal-300 px-8 py-6 text-lg rounded-full shadow-lg font-semibold"
                    data-testid="hero-cta"
                  >
                    Find My Career Gap — Free
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                
                {/* Microcopy */}
                <p className="text-sm text-slate-400 mt-3">
                  3 minutes · 12 quick questions · Instant result
                </p>
              </div>

              {/* Secondary text CTA */}
              <div className="mt-6">
                <a 
                  href="#services" 
                  className="inline-flex items-center text-sm text-teal-300 hover:text-white transition-colors"
                >
                  I already know what I need
                  <ArrowRight className="ml-1 w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Right: Sample Result Card */}
            <div className="hidden lg:block">
              <Link to="/career-readiness-check" className="block group">
                <div className="bg-white rounded-2xl shadow-2xl overflow-hidden transform group-hover:scale-[1.02] transition-transform duration-300 cursor-pointer max-w-sm mx-auto">
                  <div className="bg-slate-800 px-5 py-3">
                    <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Sample Result</span>
                  </div>
                  <div className="p-5 space-y-3">
                    {[
                      { label: 'Career Direction', score: 78, color: 'bg-emerald-500' },
                      { label: 'Profile Strength', score: 54, color: 'bg-amber-500' },
                      { label: 'Interview Readiness', score: 42, color: 'bg-orange-500', priority: true },
                      { label: 'Job Search', score: 67, color: 'bg-blue-500' },
                    ].map((item) => (
                      <div key={item.label}>
                        <div className="flex justify-between items-center mb-1">
                          <span className={`text-xs ${item.priority ? 'text-slate-900 font-semibold' : 'text-slate-500'}`}>
                            {item.label}
                          </span>
                          <span className={`text-xs font-bold ${item.priority ? 'text-orange-600' : 'text-slate-700'}`}>
                            {item.score}%
                          </span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full ${item.color} rounded-full`} style={{ width: `${item.score}%` }} />
                        </div>
                      </div>
                    ))}
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mt-3">
                      <p className="text-[10px] font-semibold text-orange-600 uppercase tracking-wider">Priority</p>
                      <p className="text-sm font-bold text-slate-900">Interview Readiness</p>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================
          SECTION 2: WHERE ARE YOU STUCK?
          ============================================ */}
      <section className="py-14 lg:py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 text-center mb-10">
            Where are you stuck?
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {stuckCards.map((card) => (
              <Link
                key={card.id}
                to={card.href}
                className="group bg-white border border-slate-200 hover:border-teal-300 rounded-2xl p-6 transition-all duration-300 hover:shadow-lg"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
                    <card.icon className="w-6 h-6 text-teal-700" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">
                      {card.title}
                    </h3>
                    <span className="inline-flex items-center text-teal-700 font-medium text-sm group-hover:gap-2 transition-all">
                      {card.cta}
                      <ArrowRight className="ml-1 w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          SECTION 3: CAREER SUPPORT (Services)
          ============================================ */}
      <section id="services" className="py-14 lg:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 text-center mb-3">
            Career Support
          </h2>
          <p className="text-slate-500 text-center mb-2 max-w-xl mx-auto">
            Choose the level of support that fits where you are.
          </p>
          <p className="text-slate-400 text-sm text-center mb-10 max-w-xl mx-auto">
            Start with clarity. Build your plan. Get support executing it.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <div
                key={service.id}
                className="bg-slate-50 border border-slate-200 rounded-2xl p-6 hover:shadow-lg transition-shadow flex flex-col"
              >
                {/* Label */}
                <span className={`inline-block self-start text-[10px] font-bold tracking-wider px-2.5 py-1 rounded-full mb-3 ${service.labelColor}`}>
                  {service.label}
                </span>

                {/* Header */}
                <h3 className="text-lg font-bold text-slate-900 mb-1">{service.name}</h3>
                <div className="flex items-baseline gap-2 mb-4">
                  {service.was && <s className="text-base text-slate-400" data-testid={`service-was-price-${service.id}`}>{service.was}</s>}
                  <span className="text-2xl font-bold text-slate-900">{service.price}</span>
                  <span className="text-slate-500 text-sm">· {service.duration}</span>
                </div>

                {/* Description */}
                <p className="text-slate-600 text-sm mb-4 leading-relaxed min-h-[60px]">
                  {service.description}
                </p>

                {/* Expandable includes */}
                <button
                  onClick={() => setExpandedService(expandedService === service.id ? null : service.id)}
                  className="text-slate-500 text-xs font-medium flex items-center gap-1 mb-4 hover:text-slate-700 transition-colors"
                >
                  {expandedService === service.id ? 'Hide details' : 'See what\'s included'}
                  {expandedService === service.id ? (
                    <ChevronUp className="w-3 h-3" />
                  ) : (
                    <ChevronDown className="w-3 h-3" />
                  )}
                </button>

                {expandedService === service.id && (
                  <ul className="space-y-2 mb-4 text-sm text-slate-600">
                    {service.includes.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-teal-700 mt-0.5 flex-shrink-0">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Spacer to push CTA to bottom */}
                <div className="flex-grow"></div>

                {/* CTA */}
                <Link to={service.href}>
                  <Button className="w-full bg-[#0b1f4d] hover:bg-[#13296b] text-white rounded-xl py-3 font-medium">
                    {service.cta}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/programs" data-testid="for-students-all-programs" className="inline-flex items-center gap-1.5 text-sm font-semibold text-teal-700 hover:text-teal-800">
              See all programs & pricing <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============================================
          SECTION 4: STUDENT PROOF
          ============================================ */}
      <section className="py-14 lg:py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl font-bold text-slate-900 text-center mb-8">
            Students who found their gap
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-xl p-5">
                <span className="inline-block bg-teal-50 text-teal-700 text-xs font-semibold px-2.5 py-1 rounded-full mb-3">
                  {t.outcome}
                </span>
                <p className="text-slate-700 text-sm leading-relaxed mb-3">"{t.quote}"</p>
                <p className="text-slate-900 font-semibold text-sm">{t.name}</p>
                <p className="text-slate-500 text-xs">{t.detail}</p>
              </div>
            ))}
          </div>

          {/* CTA under proof */}
          <div className="text-center">
            <Link to="/career-readiness-check">
              <Button className="bg-teal-400 hover:bg-teal-300 text-[#0b1f4d] px-8 py-4 rounded-full font-semibold">
                Find My Career Gap — Free
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ============================================
          SECTION 5: TALENT NETWORK
          ============================================ */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
            Already career-ready?
          </h2>
          <p className="text-lg text-teal-700 font-medium mb-4">
            Get discovered for relevant opportunities.
          </p>
          <p className="text-slate-600 mb-8 max-w-lg mx-auto">
            Join the Elevate Talent Network and become part of our growing candidate community.
          </p>

          <Link to="/network">
            <Button className="bg-[#0b1f4d] hover:bg-[#13296b] text-white px-8 py-5 text-lg rounded-full font-semibold">
              Join Talent Network — Free
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>

          {/* Disclaimer */}
          <p className="text-xs text-slate-400 mt-6 max-w-md mx-auto">
            Joining the Elevate Talent Network does not guarantee interviews or employment. Candidates may be considered based on role fit and employer requirements.
          </p>
        </div>
      </section>
    </div>
  );
};

export default ForStudents;
